import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { firestore as db, auth } from "../FirebaseConfig";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Reviews from "./Review";
import QuantitySelector from "./QuantitySelector"; // ← kept exactly as you used it
import Description from "./Description";
import AdditionalInfo from "./AdditionalInfo";
import BestSellingProduct from "./PopularProducts";
import Lottie from "lottie-react";
import toast from "react-hot-toast";
import PincodeETA from "./PincodeETA"; // or wherever you placed it
import HoneyBackground from "./HoneyBg";
import BeeScrollProgress from "../Home_Page/components/BeeScrollProgress";

// =======================
// Config
// =======================

// =======================
// UI helpers
// =======================
const PriceBlock = ({ price = 0, discount = 0 }) => {
  const final = Math.max(0, price - discount);
  const pct = price > 0 ? Math.round((discount / price) * 100) : 0;

  return (
    <div className="flex items-end flex-wrap gap-3">
      <div className="text-3xl sm:text-4xl font-bold text-gray-900">₹{final}</div>

      {discount > 0 && (
        <>
          <div className="text-gray-500 line-through text-lg sm:text-xl">₹{price}</div>
          {/* Highlighted percent OFF */}
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-700 font-semibold text-sm shadow-sm">
          <svg width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 3H5l-2 4v6a8 8 0 0 0 16 0V7zM7 9h10v4a5 5 0 0 1-10 0z"/></svg>
          {pct}% OFF
        </span>
          <div className="text-gray-600 text-sm sm:text-base">You save ₹{discount}</div>
        </>
      )}

     
    </div>
  );
};

const StockBadge = ({ stock }) => {
  if (stock === 0) {
    return <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">Out of stock</span>;
  }
  if (stock <= 10) {
    return <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">Only {stock} left</span>;
  }
  return <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">In stock</span>;
};

const ShareButton = ({ title, text, url }) => {
  const canShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const onShare = async () => {
    const shareUrl = url || window.location.href;
    try {
      if (canShare) {
        await navigator.share({
          title: title || document.title,
          text: text || "",
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied!");
      }
    } catch {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied!");
      } catch {
        toast.error("Unable to share right now.");
      }
    }
  };

  return (
    <button
      onClick={onShare}
      className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 text-gray-700 text-xs sm:text-sm font-medium shadow-sm transition"
      aria-label="Share this product"
    >
      {/* Arrow-out-of-box style share icon */}
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        className="shrink-0"
        aria-hidden="true"
      >
        <path
          fill="currentColor"
          d="M14 3a1 1 0 0 0 0 2h3.586l-6.293 6.293a1 1 0 0 0 1.414 1.414L19 6.414V10a1 1 0 1 0 2 0V4a1 1 0 0 0-1-1z"
        />
        <path
          fill="currentColor"
          d="M5 5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5a1 1 0 1 0-2 0v5H5V7h5a1 1 0 1 0 0-2z"
        />
      </svg>
      <span>Share</span>
    </button>
  );
};


const ProductSkeleton = () => (
  <div className="container mx-auto px-4 py-8 mt-20">
    <div className="grid grid-cols-1 md:grid-cols-2 md:gap-8 items-start">
      <div className="rounded-lg aspect-[4/3] w-full bg-gray-100 animate-pulse" />
      <div className="space-y-4">
        <div className="h-8 w-2/3 bg-gray-100 rounded animate-pulse" />
        <div className="h-4 w-1/3 bg-gray-100 rounded animate-pulse" />
        <div className="h-24 w-full bg-gray-100 rounded animate-pulse" />
        <div className="h-10 w-2/5 bg-gray-100 rounded animate-pulse" />
        <div className="flex gap-3">
          <div className="h-11 w-32 bg-gray-100 rounded animate-pulse" />
          <div className="h-11 w-32 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    </div>
  </div>
);


// =======================
// Main
// =======================
const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(id);
  const [quantity, setQuantity] = useState(1); // ← kept original behavior
  const [stockWarning, setStockWarning] = useState("");
  const [addingToCart, setAddingToCart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [animationData, setAnimationData] = useState(null);
  const [thumbIndex, setThumbIndex] = useState(0);

  const mainSwiperRef = useRef(null);

  
  useEffect(() => {
    window.scrollTo(0, 0);
 
    const fetchProduct = async () => {
      if (!id) return;
      
     const renderWidget = () => {
      // Check if library is loaded
      if (!window.RazorpayAffordabilitySuite) return;

      // CRITICAL: Check if the DIV exists in the DOM
      const widgetContainer = document.getElementById("razorpay-affordability-widget");
      
      if (!widgetContainer) {
        // If div not found, wait 500ms and try again (DOM might be painting)
        console.warn("Widget container not found, retrying...");
        setTimeout(renderWidget, 500);
        return;
      }

      // If div exists, clear it (prevents duplicates) and render
      widgetContainer.innerHTML = ""; 
      
      try {
        const widgetConfig = {
          key: "rzp_live_RVhSM9gWpyxUzy", // <--- REPLACE WITH YOUR TEST/LIVE KEY
          amount: 800000,
        };
        const rzpAffordabilitySuite = new window.RazorpayAffordabilitySuite(widgetConfig);
        rzpAffordabilitySuite.render();
      } catch (err) {
        console.error("Razorpay Widget Error:", err);
      }
    };

    // 4. Load script if needed, otherwise just render
    const scriptUrl = "https://cdn.razorpay.com/widgets/affordability/affordability.js";
    if (!document.querySelector(`script[src="${scriptUrl}"]`)) {
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;
      script.onload = renderWidget;
      document.body.appendChild(script);
    } else {
      // Script already loaded? Just render.
      renderWidget();
    }

      try {
        const productRef = doc(db, "products", id);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          const productData = productSnap.data();
          setProduct(productData);

          if (Array.isArray(productData?.variants) && productData.variants.length > 0) {
            await fetchVariants(productData.variants);
          } else {
            setVariants([]);
          }
        } else {
          setProduct(null);
        }
      } catch (err) {
        console.error("Error fetching product:", err);
        toast.error("Unable to load product.");
        setProduct(null);
      } finally {
        setLoading(false);
        window.scrollTo(0, 0);
      }
    };

    const fetchVariants = async (variantArray) => {
      const out = [];
      for (const v of variantArray) {
        if (!v?.id) continue;
        const vRef = doc(db, "products", v.id);
        const vSnap = await getDoc(vRef);
        if (vSnap.exists()) {
          const vd = vSnap.data();
          const price = vd?.prices?.productPrice || 0;
          const discount = vd?.prices?.productDiscount || 0;
          out.push({
            id: v.id,
            stock: vd?.stock || 0,
            weight: vd?.weight,
            price,
            discount,
            final: Math.max(0, price - discount),
            discountPercentage: price > 0 ? Math.round((discount / price) * 100) : 0,
          });
        }
      }
      out.sort((a, b) => {
        const wa = Number(a.weight ?? 0);
        const wb = Number(b.weight ?? 0);
        if (wa && wb) return wa - wb;
        return a.final - b.final;
      });
      setVariants(out);
    };

    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product && quantity > product.stock) {
      setStockWarning(`Only ${product.stock} items available in stock.`);
    } else {
      setStockWarning("");
    }
  }, [quantity, product]);

  useEffect(() => {
    fetch("/assets/General/loadingBee.json")
      .then((r) => r.json())
      .then((d) => setAnimationData(d))
      .catch(() => {});
  }, []);

  const handleVariantChange = (variantId) => {
    if (!variantId) return;
    setSelectedVariant(variantId);
    navigate(`/product/${variantId}`);
  };

  const handleBuyNow = () => {
    if (!product) return;
    if (quantity > product.stock) {
      toast.error("Selected quantity exceeds stock availability.");
      return;
    }
    navigate(`/checkout?productId=${id}&quantity=${quantity}`);
  };

  const handleAddToCart = async () => {
    if (!auth.currentUser) {
      toast("Please log in to add items to your cart.");
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`);
      return;
    }
    if (!product) return;
    if (quantity > product.stock) return;

    setAddingToCart(true);
    const userId = auth.currentUser.uid;
    const cartRef = doc(db, "customers", userId);

    try {
      const cartSnap = await getDoc(cartRef);
      if (cartSnap.exists()) {
        const cartData = cartSnap.data() || {};
        const items = Array.isArray(cartData.items) ? [...cartData.items] : [];
        const idx = items.findIndex((it) => it.productId === id);
        if (idx >= 0) {
          const newQty = (items[idx].quantity || 0) + quantity; // ← original add-up behavior
          items[idx] = { ...items[idx], quantity: newQty };
        } else {
          items.push({ productId: id, quantity });
        }
        await updateDoc(cartRef, { ...cartData, items });
      } else {
        await setDoc(cartRef, { items: [{ productId: id, quantity }] });
      }
      toast.success("Added to cart!");
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Error adding item to cart. Please try again.");
    } finally {
      setAddingToCart(false);
    }
  };


const ShimmerImage = ({ src, alt, className, containerClassName }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    // If the image is already cached, onLoad might not fire. 
    // We check the .complete property manually.
    if (imgRef.current?.complete) {
      setIsLoaded(true);
    }
  }, [src]);

  return (
    <div className={`relative overflow-hidden ${containerClassName} ${!isLoaded ? "bg-gray-200 animate-pulse" : ""}`}>
      {/* The Shimmer Overlay */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
      )}
      
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`${className} transition-opacity w-full duration-500 ${isLoaded ? "opacity-100" : "opacity-0"}`}
        onLoad={() => setIsLoaded(true)}
        onError={(e) => {
          e.currentTarget.src = "/assets/General/placeholder.png";
          setIsLoaded(true);
        }}
      />
    </div>
  );
};
  const price = product?.prices?.productPrice || 0;
  const discount = product?.prices?.productDiscount || 0;
  const discountPercent = price > 0 ? Math.round((discount / price) * 100) : 0;

  const images = useMemo(() => {
    const arr = Array.isArray(product?.images) ? product.images : [];
    return arr.length > 0 ? arr : ["/assets/General/placeholder.png"];
  }, [product]);

  const productJsonLd = useMemo(() => {
    if (!product) return null;
    const final = Math.max(0, price - discount);
    return {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product?.name,
      image: images,
      description: product?.description,
      category: product?.category,
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        price: String(final || 0),
        availability: product?.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        url: typeof window !== "undefined" ? window.location.href : "",
      },
    };
  }, [product, price, discount, images]);

  if (loading) {
    return animationData ? (
      <div className="h-screen flex justify-center items-center">
        <Lottie animationData={animationData} loop autoPlay style={{ width: "50%", maxWidth: 300 }} />
      </div>
    ) : (
      <ProductSkeleton />
    );
  }

  if (!product) {
    return (
      <div className="h-screen flex justify-center items-center text-xl">
        Product not found.
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 mt-25">

      <BeeScrollProgress/>

            <HoneyBackground />

      {/* JSON-LD */}
      {productJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd) }} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start">
        {/* IMAGES */}
        <div className="relative">
          {/* Discount ribbon over image when applicable */}
          {discount > 0 && (
            
             <span className="absolute z-10 top-3 left-3 px-2 py-1   rounded-full bg-green-100 text-green-700 font-semibold text-sm shadow-sm">
          {/* <svg width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 3H5l-2 4v6a8 8 0 0 0 16 0V7zM7 9h10v4a5 5 0 0 1-10 0z"/></svg> */}
          {discountPercent}% OFF
        </span>
          )}

          <Swiper
            onSwiper={(s) => (mainSwiperRef.current = s)}
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={10}
            slidesPerView={1}
            pagination={{ clickable: true }}
            autoplay={{ delay: 3200, disableOnInteraction: false }}
            className="rounded-xl shadow-md bg-white"
          >
            {images.map((image, idx) => (
          
<SwiperSlide key={idx}>
 <div className="flex justify-center items-center w-full aspect-square ">
        <ShimmerImage
          src={image}
          alt={`${product.name} - image ${idx + 1}`}
          containerClassName="w-full h-full"
          className="w-full h-full object-contain"
        />
      </div>
</SwiperSlide>
            ))}
          </Swiper>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="mt-3 grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-2">
              {images.map((thumb, idx) => (
<button
  key={`thumb-${idx}`}
  onClick={() => {
    setThumbIndex(idx);
    if (mainSwiperRef.current) mainSwiperRef.current.slideTo(idx);
  }}
  className={`border rounded-lg p-1 transition ${

thumbIndex === idx ? "border-orange-500 ring-2 ring-orange-200" : "border-gray-200 hover:border-gray-300"

}`}

aria-label={`Go to image ${idx + 1}`}

>
 
  <ShimmerImage
    src={thumb}
    alt={`Thumbnail ${idx + 1}`}
    containerClassName="h-16 w-full rounded-md"
    className="h-full w-full object-cover"
  />
</button>
              ))}
            </div>
          )}
        </div>

        {/* DETAILS */}
        <div className="w-full">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {product.name}
              <StockBadge stock={product.stock || 0} />
            </h1>
            <ShareButton
              title={product.name}
              text={product.description}
              url={typeof window !== "undefined" ? window.location.href : ""}
            />
          </div>

          <div className="mt-2 text-sm sm:text-base text-gray-600">
            Category: <span className="font-medium text-gray-800">{product.category}</span>
          </div>

          {/* MOBILE: amount block appears right after images (this is already after images in DOM order).
              To emphasize on mobile, we place PriceBlock before the description and variant sections. */}
          <div className="mt-4">
            <PriceBlock price={product?.prices?.productPrice || 0} discount={product?.prices?.productDiscount || 0} />
          </div>

{/* We add a small margin-top (mt-3) to separate it from price */}
          <div className="mt-3 mb-4" style={{ minHeight: '40px' }}>
             <div id="razorpay-affordability-widget"></div>
          </div>

          {/* {product.description && (
            <p className="text-gray-700 mt-3 text-sm sm:text-base leading-relaxed">
              {product.description}
            </p>
          )} */}

{product.description && (
  <p className="text-gray-700 mt-3 text-sm sm:text-base leading-relaxed text-left">
    {product.description}
  </p>
)}
          {/* Variants */}
          {variants.length > 0 && (
            <div className="mt-6">
              <h3 className="text-base sm:text-lg font-semibold mb-2">Select a Variant</h3>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {variants.map((v) => {
                  const isSelected = selectedVariant === v.id;
                  const disabled = v.stock === 0;
                  return (
                    <button
                      key={v.id}
                      onClick={() => handleVariantChange(v.id)}
                      disabled={disabled}
                      className={[
                        "relative border p-3 rounded-lg text-sm font-medium transition text-center focus:outline-none focus:ring-2",
                        disabled
                          ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          : isSelected
                          ? "bg-green-50 text-green-700 border-green-500 ring-green-200"
                          : "bg-white text-gray-700 border-gray-200 hover:border-orange-400 hover:bg-orange-50"
                      ].join(" ")}
                      aria-pressed={isSelected}
                    >
                      <div className="font-semibold">{(Number(v.weight) || 0) * 1000} g</div>
                      <div className="text-xs text-gray-500 mt-1">
                        ₹{v.final} {v.discount > 0 && <>({v.discountPercentage}% OFF)</>}
                      </div>
                      {v.stock <= 10 && v.stock > 0 && (
                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[10px] px-2 py-[2px] rounded-full bg-red-50 text-red-600 border border-red-100">
                           {v.stock} left
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ============ Quantity (kept like your old one) ============ */}
          <div className="mt-4">
            <QuantitySelector quantity={quantity} setQuantity={setQuantity} />
            {stockWarning && <p className="text-red-500 text-sm mt-1">{stockWarning}</p>}
          </div>

          {/* Pincode ETA */}

          {/* Desktop CTAs */}
         {/* Desktop CTAs */}
<div className="hidden md:block mt-6">
  <div className="max-w-xl flex gap-4">
    {/* Add to Cart */}
    <button
      onClick={handleAddToCart}
      className={[
        "flex-1 inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-full text-sm font-semibold transition",
        addingToCart || quantity > product.stock
          ? "bg-gray-200 text-gray-500 cursor-not-allowed"
          : "bg-amber-50 text-amber-800 border border-amber-300 hover:bg-amber-100 hover:border-amber-400 shadow-sm",
      ].join(" ")}
      disabled={addingToCart || quantity > product.stock}
    >
      {/* Cart icon */}
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="shrink-0"
      >
        <path
          fill="currentColor"
          d="M7 4a1 1 0 0 0-.98.804L5.11 9H4a1 1 0 1 0 0 2h1.11l1.2 6.402A2 2 0 0 0 8.277 19h7.446a2 2 0 0 0 1.968-1.598L19.89 9H20a1 1 0 1 0 0-2h-1.11l-.91-4.196A1 1 0 0 0 17 2H7zm1.24 2h7.52l.65 3.196L16.277 17H7.723L6.59 9.196zM9 21a1.5 1.5 0 1 1-3 0a1.5 1.5 0 0 1 3 0m9 0a1.5 1.5 0 1 1-3 0a1.5 1.5 0 0 1 3 0"
        />
      </svg>
      <span>{addingToCart ? "Adding..." : "Add to Cart"}</span>
    </button>

    {/* Buy Now */}
    <button
      onClick={handleBuyNow}
                  //  className="bg-green-600 text-white py-3 px-6 rounded-md hover:bg-green-700 hover:shadow-lg active:scale-95 transition duration-300 disabled:opacity-60"

      className={[
        "flex-1 inline-flex items-center bg-green-600  justify-center gap-2 py-3.5 px-6 rounded-full text-sm font-semibold transition",
        quantity > product.stock
          ? "bg-gray-300 text-gray-600 cursor-not-allowed"
          : "bg-amber-600 text-white hover:bg-amber-700 shadow-md hover:shadow-lg",
      ].join(" ")}
      disabled={quantity > product.stock}
    >
      {/* Lightning icon */}
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="shrink-0"
      >
        <path
          fill="gree"
          d="M13 2L5 14h5l-1 8l8-12h-5z"
        />
      </svg>
      <span>Buy Now</span>
    </button>
  </div>

  <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
    <span className="inline-flex h-3 w-3 rounded-full bg-emerald-500" />
    <span>Secure payments </span>
  </p>
</div>

          <PincodeETA productId={id} />

        </div>
      </div>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 z-50 left-0 right-0 sm:hidden bg-white/90 backdrop-blur border-t p-3 flex justify-center items-center shadow-[0_-8px_24px_rgba(0,0,0,0.06)]">
        <div className="w-full max-w-lg flex gap-3">
          <button
            onClick={handleAddToCart}
            className={`flex-1 py-3 rounded-full text-center font-semibold transition ${
              addingToCart || quantity > product.stock
                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                : "bg-orange-500 text-white hover:bg-orange-600 active:scale-95"
            }`}
            disabled={addingToCart || quantity > product.stock}
          >
            {addingToCart ? "Adding..." : "Add to Cart"}
          </button>

          <button
            onClick={handleBuyNow}
            className="flex-1 py-3 rounded-full text-center font-semibold bg-green-600 text-white hover:bg-green-700 active:scale-95 transition disabled:opacity-60"
            disabled={quantity > product.stock}
          >
            ⚡ Buy Now
          </button>
        </div>
      </div>

      {/* Content */}

      <div className="mt-10">
        <Description htmlString={product.longDescription} />
      </div>

      {/* {product && <AdditionalInfo weight={product.weight ? parseFloat(product.weight) : 0} />} */}

{/* {product && (
  <AdditionalInfo
    weight={product.weight ? parseFloat(product.weight) : 0}
    beesUsed={product.beesUsed}             // e.g. "Apis mellifera, Apis cerana"
    harvestLocation={product.harvestLocation} // e.g. "KBR Park Apiary, Hyderabad"
    segmentation={product.segmentation}     // e.g. "Raw • Uni-floral • Cold-extracted"
    ingredients={product.ingredients}       // optional – falls back to your old text
    shelfLife={product.shelfLife}          // optional – falls back to "24 months"
    storage={product.storage}              // optional – falls back to your old text
    mapImage={product.mapImage}            // optional – defaults to /assets/General/india.png
    mapX={product.mapX}                    // 0–100 (% across image width)
    mapY={product.mapY}                    // 0–100 (% down image height)
  />
)} */}

   <div className="mt-10">
        <BestSellingProduct />
      </div>
<AdditionalInfo
  weight={product.weight}
  beesUsed={product.beesUsed}
  harvestLocation={product.harvestLocation}
  segmentation={product.segmentation}
  ingredients={product.ingredients}
  shelfLife={product.shelfLife}
  storage={product.storage}
  mapImage={product.mapImage}
  mapX={product.mapX}
  mapY={product.mapY}
  logos={[
    "/assets/General/ad1.png",
    "/assets/General/ad2.png",
    "/assets/General/ad3.png",
    "/assets/General/ad4.png",
    "/assets/General/ad5.png",
  ]}
/>




   

      <div className="mt-6">
        {/* <Reviews id={id} /> */}
        <Reviews reviews={product.reviews || []} />
      </div>
    </div>
  );
};

export default ProductPage;
